/**
 * Admin Service Module
 * Provides API service functions for administrative operations including clause management,
 * taxonomy management, data explorer operations, and deployment information
 * All functions make async HTTP calls to the backend API server
 */

// services/adminservice.js
import {API_SERVER_URL} from "../utils/config_ip_details";
import {apiCall,apiCallAsync} from "../utils/server_utils"

/**
 * Retrieves all clauses from the database
 * @returns {Promise} Promise containing all clause records
 */
export const getAllClauses = async () => {
  return apiCall("GET", API_SERVER_URL+"clause/");
};

/**
 * Retrieves all taxonomy audit logs from the server
 * @returns {Promise} Promise containing audit log records
 */
export const getAllTaxonomyAuditLogs = async () => {
  return apiCall("GET", API_SERVER_URL+"taxonomy/audit-log/");
}

/**
 * Retrieves recent taxonomy audit logs (limited time period)
 * @returns {Promise} Promise containing recent audit log records
 */
export const getRecentTaxonomyAuditLogs = async () => {
  return apiCall("GET", API_SERVER_URL+"taxonomy/recent-log/");
}

/**
 * Fetches all taxonomy data from the server
 * @returns {Promise} Promise containing taxonomy records
 */
export const fetchTaxonomyData = async () => {
  return apiCall("GET", API_SERVER_URL+"taxonomy/");
};

/**
 * Saves or updates taxonomy data on the server
 * @param {Object} taxonomyData - The taxonomy data object to save/update
 * @returns {Promise} Promise containing the save operation result
 */
export const saveTaxonomyData = async (taxonomyData) => {
  return apiCall("POST", API_SERVER_URL+"taxonomy-update/", taxonomyData);
};

/**
 * Deletes a specific clause by its ID
 * @param {string|number} delId - The ID of the clause to delete
 * @returns {Promise} Promise containing the delete operation result
 */
export const deleteClause = async (delId) => {
  return apiCall("POST", API_SERVER_URL+"clause/"+delId+"/delete/");
};

/**
 * Creates a new clause with the provided data
 * @param {Object} createObject - The clause data object to create
 * @returns {Promise} Promise containing the newly created clause
 */
export const createClause = async (createObject) => {
  return apiCall("POST", API_SERVER_URL+"clause/",createObject);
};

/**
 * Updates an existing clause by ID with new data
 * @param {string|number} updateId - The ID of the clause to update
 * @param {Object} updateObject - The updated clause data object
 * @returns {Promise} Promise containing the updated clause
 */
export const updateClause = async (updateId,updateObject) => {
  return apiCall("PUT", API_SERVER_URL+"clause/detail/"+updateId+"/",updateObject);
};


/**
 * Retrieves all taxonomy records from the server
 * @returns {Promise} Promise containing all taxonomy data
 */
export const getAllTaxonomy = async () => {
  return apiCall("GET", API_SERVER_URL+"taxonomy/");
};

/**
 * Deletes a specific taxonomy record by its ID
 * @param {string|number} delId - The ID of the taxonomy to delete
 * @returns {Promise} Promise containing the delete operation result
 */
export const deleteTaxonomy = async (delId) => {
  return apiCall("POST", API_SERVER_URL+"taxonomy/"+delId+"/delete/");
};

/**
 * Creates a new taxonomy record with the provided data
 * @param {Object} createObject - The taxonomy data object to create
 * @returns {Promise} Promise containing the newly created taxonomy
 */
export const createTaxonomy = async (createObject) => {
  return apiCall("POST", API_SERVER_URL+"taxonomy/",createObject);
};

/**
 * Updates an existing taxonomy record by ID with new data
 * @param {string|number} updateId - The ID of the taxonomy to update
 * @param {Object} updateObject - The updated taxonomy data object
 * @returns {Promise} Promise containing the updated taxonomy
 */
export const updateTaxonomy = async (updateId,updateObject) => {
  return apiCall("PUT", API_SERVER_URL+"taxonomy/detail/"+updateId+"/",updateObject);
};

/**
 * Retrieves licensor data for the data explorer view
 * @returns {Promise} Promise containing licensor data
 */
export const getLicensorDataExploerView = async () => {
  return apiCall("GET", API_SERVER_URL+"data-explorer/licensors/");
};

/**
 * Retrieves detailed data explorer information (V2 API)
 * @param {Object} createObject - Filter/query object for data explorer details
 * @returns {Promise} Promise containing detailed data explorer records
 */
export const getDataExplorerDetails = async (createObject) => {
  return apiCall("POST", API_SERVER_URL+"data-explorer/details-v2/",createObject);
}
/**
 * DEPRECATED: Retrieves detailed data explorer information (legacy API)
 * @param {Object} createObject - Filter/query object for data explorer details
 * @returns {Promise} Promise containing detailed data explorer records
 * @deprecated Use getDataExplorerDetails instead
 */
export const getDataExplorerDetails_old = async (createObject) => {
  return apiCall("POST", API_SERVER_URL+"data-explorer/details/",createObject);
};


/**
 * Retrieves Ask Genie results based on the provided query/object
 * @param {Object} createObject - Query object for Ask Genie search/analysis
 * @returns {Promise} Promise containing Ask Genie analysis results
 */
export const getAskGenieResults = async (createObject) => {
  return apiCall("POST", API_SERVER_URL+"disney-contract/",createObject);
};

/**
 * Retrieves markdown/HTML content for a specific contract by ID
 * @param {string|number} pmid - The contract/document ID
 * @returns {Promise} Promise containing the markdown/HTML content
 */
export const getMarkDownHtml = async (pmid) => {
  return apiCall("GET", API_SERVER_URL+"contracts/"+pmid+"/");
};
/**
 * Retrieves page access permissions for the current user
 * Returns access control information for various application pages
 * @returns {Promise} Promise containing user page access and permissions
 */export const pageAccess = async () => {
  return apiCall("GET", API_SERVER_URL+"user/page-access");
};
/**
 * Retrieves data explorer portfolio/dimensions information
 * Includes product, channel, territory, and other dimensional data
 * @param {Object} obj - Query parameters for filtering portfolio data
 * @returns {Promise} Promise containing portfolio and dimensional data
 */
export const getDataExplorerPortfolio = async (obj) => {
  //alert(JSON.stringify(obj));
  //return apiCall("POST", API_SERVER_URL+"data-explorer/dimensions/",{"propertyType":[]});
  return apiCall("POST", API_SERVER_URL+"data-explorer/dimensions/",obj);
};

/**
 * Retrieves data explorer restrictions/exceptions data
 * Used to manage product/territory restrictions and limitations
 * @param {Object} obj - Query parameters for filtering restrictions data
 * @returns {Promise} Promise containing restrictions and exception records
 */
export const getDataExplorerException = async (obj) => {
  return apiCall("POST", API_SERVER_URL+"data-explorer/restrictions/",obj);
};


/**
 * Retrieves data explorer dashboard summary information
 * Includes counts and hierarchical data for channels, products, territories, etc.
 * @param {Object} askaiquery - Optional query object for dashboard filtering
 * @returns {Promise} Promise containing dashboard summary and dimensional data
 */
export const getDataExploerDahsboard = async (askaiquery) => {
  return apiCall("POST", API_SERVER_URL+"data-explorer/portfolio/",{});
} 




/**
 * Alternative data explorer dashboard retrieval with optional query parameters
 * Supports both parameterized and non-parameterized requests
 * @param {Object} askaiquery - Optional query object for dashboard filtering
 * @returns {Promise} Promise containing dashboard data
 */
export const getDataExploerDahsboard_1 = async (askaiquery) => {
  const url = API_SERVER_URL + "data-explorer/dashboard/";
  return askaiquery
    ? apiCall("POST", url, askaiquery)
    : apiCall("POST", url);
    //  return apiCall("GET", API_SERVER_URL+"data-explorer/dashboard/", askaiquery);
}


/**
 * Retrieves deployment and version information
 * Currently returns mock data; can be updated to call actual API endpoint
 * @returns {Promise} Promise containing deployment version and date information
 */
export const getDeploymentInfo = async () => {
  //return apiCall("GET", API_SERVER_URL+"deployment-info/");
  return ({
    "success": true,
    "data": {
        "version": "2.0.6",
        "deploymentDate": "13-Jan-2026"
    }
})
}
